import { HTMLAttributes, ReactNode } from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import styles from "./Badge.module.scss";

export type BadgeVariant = "primary" | "secondary" | "neutral" | "disabled";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
    children: ReactNode;
    variant?: BadgeVariant;
    iconLeft?: ReactNode;
}

export interface BadgeCounterProps extends HTMLAttributes<HTMLSpanElement> {
    value?: number;
    variant?: BadgeVariant;
    max?: number;
    dot?: boolean;
}

export type QualityScoreTone = "good" | "medium" | "bad";

export interface QualityScoreBadgeProps
    extends HTMLAttributes<HTMLSpanElement> {
    score: number;
    tone?: QualityScoreTone;
}

export interface VerifiedBadgeProps extends HTMLAttributes<HTMLSpanElement> {
    size?: "sm" | "lg";
    label?: string;
}

const CheckIcon = () => <Icon icon="check" />;

const BadgeIcon = ({ children }: { children: ReactNode }) => (
    <span aria-hidden className={styles.icon}>
        {children}
    </span>
);

/** Badge (content tag) — Figma node family around `7339:52753`. */
export const Badge = ({
    children,
    variant = "primary",
    iconLeft,
    className,
    ...rest
}: BadgeProps) => (
    <span
        className={cx(styles.badge, className)}
        data-ui="badge"
        data-variant={variant}
        {...rest}
    >
        {iconLeft ? <BadgeIcon>{iconLeft}</BadgeIcon> : null}
        {children}
    </span>
);

/** Badge Counter — Figma docs node `10851:60524`. */
export const BadgeCounter = ({
    value = 0,
    variant = "primary",
    max = 99,
    dot = false,
    className,
    ...rest
}: BadgeCounterProps) => {
    const display = value > max ? `${max}+` : String(value);
    return (
        <span
            className={cx(styles.counter, className)}
            data-dot={dot || undefined}
            data-ui="badge-counter"
            data-variant={variant}
            {...rest}
        >
            {dot ? null : display}
        </span>
    );
};

/** Quality score badge — Figma `7339:52834`. */
export const QualityScoreBadge = ({
    score,
    tone,
    className,
    ...rest
}: QualityScoreBadgeProps) => {
    const resolvedTone: QualityScoreTone =
        tone ?? (score >= 8 ? "good" : score >= 5 ? "medium" : "bad");

    return (
        <span
            className={cx(styles.quality, className)}
            data-tone={resolvedTone}
            data-ui="quality-score-badge"
            {...rest}
        >
            {score.toFixed(1)}
        </span>
    );
};

/** Verified badge — Figma `6766:53858` (small icon / large with label). */
export const VerifiedBadge = ({
    size = "sm",
    label = "Verified",
    className,
    ...rest
}: VerifiedBadgeProps) => (
    <span
        className={cx(styles.verified, className)}
        data-size={size}
        data-ui="verified-badge"
        {...rest}
    >
        <span className={styles.verifiedIcon}>
            <CheckIcon />
        </span>
        {size === "lg" ? label : null}
    </span>
);
